import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Box,
  Check,
  X,
  Clock,
  Key,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";

const SandboxPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedItem, setSelectedItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);

  // Service tokens
  const [tokens, setTokens] = useState([]);
  const [showTokens, setShowTokens] = useState(false);
  const [newTokenName, setNewTokenName] = useState("openclaw");
  const [createdToken, setCreatedToken] = useState(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/sandbox?status=${statusFilter}`);
      setItems(res.data || []);
    } catch {
      toast.error("Failed to load sandbox items");
    } finally {
      setLoading(false);
    }
  };

  const fetchTokens = async () => {
    try {
      const res = await api.get("/admin/service-tokens");
      setTokens(res.data || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => { fetchItems(); }, [statusFilter]);

  const handleAccept = async (id) => {
    try {
      await api.post(`/sandbox/${id}/accept`);
      toast.success("Accepted and applied");
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
    }
  };

  const handleReject = async () => {
    try {
      await api.post(`/sandbox/${rejectingId}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setShowRejectDialog(false);
      setRejectReason("");
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const handleCreateToken = async () => {
    try {
      const res = await api.post("/admin/service-tokens", { name: newTokenName });
      setCreatedToken(res.data.token);
      toast.success("Token created");
      fetchTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleRevokeToken = async (id) => {
    try {
      await api.delete(`/admin/service-tokens/${id}`);
      toast.success("Token revoked");
      fetchTokens();
    } catch {
      toast.error("Failed");
    }
  };

  const stats = useMemo(() => ({
    pending: items.length,
  }), [items]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Box className="h-7 w-7 text-cyan-400" />
              Sandbox
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              External API data awaiting review
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowTokens(!showTokens); if (!showTokens) fetchTokens(); }}
              className="border-zinc-700 text-zinc-300"
              data-testid="manage-tokens-btn"
            >
              <Key className="h-3.5 w-3.5 mr-1.5" />
              API Tokens
            </Button>
            <Button size="sm" variant="ghost" onClick={fetchItems} className="text-zinc-400">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Token Management Panel */}
        {showTokens && (
          <Card className="bg-zinc-900/80 border-zinc-700">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-zinc-300">Service Tokens</h3>
                <div className="flex items-center gap-2">
                  <Input
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                    placeholder="Token name"
                    className="w-40 h-8 bg-zinc-800 border-zinc-700 text-sm"
                  />
                  <Button size="sm" onClick={handleCreateToken} className="h-8 bg-cyan-600 hover:bg-cyan-700" data-testid="create-token-btn">
                    <Plus className="h-3 w-3 mr-1" /> Generate
                  </Button>
                </div>
              </div>
              {createdToken && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-400 font-medium mb-1">New token (copy now — shown only once):</p>
                    <code className="text-sm text-zinc-50 font-mono bg-zinc-800 px-2 py-1 rounded">{createdToken}</code>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-400" onClick={() => { navigator.clipboard.writeText(createdToken); toast.success("Copied"); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {tokens.length > 0 ? (
                <div className="space-y-2">
                  {tokens.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-2 px-3 bg-zinc-800/50 rounded text-sm">
                      <div className="flex items-center gap-3">
                        <Badge className={t.active ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700 text-zinc-500"}>
                          {t.active ? "active" : "revoked"}
                        </Badge>
                        <span className="text-zinc-50 font-medium">{t.name}</span>
                        <span className="text-zinc-500 text-xs">created {t.created_at?.slice(0, 10)}</span>
                        {t.last_used && <span className="text-zinc-600 text-xs">last used {t.last_used?.slice(0, 10)}</span>}
                      </div>
                      {t.active && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:bg-red-500/10" onClick={() => handleRevokeToken(t.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-500">No tokens created yet</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Status Tabs */}
        <div className="flex items-center gap-2">
          {[
            { v: "pending", label: "Pending", color: "#facc15" },
            { v: "accepted", label: "Accepted", color: "#4ade80" },
            { v: "rejected", label: "Rejected", color: "#f87171" },
            { v: "all", label: "All", color: "#a1a1aa" },
          ].map(({ v, label, color }) => (
            <button
              key={v}
              onClick={() => setStatusFilter(v)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                statusFilter === v ? "border" : "bg-zinc-800/50 text-zinc-500 border border-zinc-800 hover:text-zinc-300"
              )}
              style={statusFilter === v ? { backgroundColor: `${color}15`, color, borderColor: `${color}40` } : {}}
              data-testid={`sandbox-tab-${v}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Items Table */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 text-center text-zinc-500">Loading...</div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-zinc-500">
                <Box className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No {statusFilter} items in sandbox</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Source</TableHead>
                    <TableHead className="text-zinc-400">Action</TableHead>
                    <TableHead className="text-zinc-400">Title</TableHead>
                    <TableHead className="text-zinc-400">Target</TableHead>
                    <TableHead className="text-zinc-400">Date</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} className="border-zinc-800 hover:bg-zinc-800/30">
                      <TableCell>
                        <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/20 text-xs">{item.source}</Badge>
                      </TableCell>
                      <TableCell className="text-zinc-50 text-sm font-medium">{item.action}</TableCell>
                      <TableCell className="text-zinc-300 text-sm max-w-[200px] truncate">{item.title || item.description || "—"}</TableCell>
                      <TableCell className="text-zinc-500 text-xs font-mono">{item.target_collection || "—"}</TableCell>
                      <TableCell className="text-zinc-500 text-xs">{item.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell>
                        <Badge className={cn("text-xs",
                          item.status === "pending" && "bg-yellow-500/20 text-yellow-400",
                          item.status === "accepted" && "bg-emerald-500/20 text-emerald-400",
                          item.status === "rejected" && "bg-red-500/20 text-red-400",
                        )}>{item.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-400 hover:text-zinc-50" onClick={() => setSelectedItem(item)} data-testid={`view-item-${item.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {item.status === "pending" && (
                            <>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400 hover:bg-emerald-500/10" onClick={() => handleAccept(item.id)} data-testid={`accept-item-${item.id}`}>
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:bg-red-500/10" onClick={() => { setRejectingId(item.id); setShowRejectDialog(true); }} data-testid={`reject-item-${item.id}`}>
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">{selectedItem?.title || selectedItem?.action}</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-500">Source:</span> <span className="text-cyan-400">{selectedItem.source}</span></div>
                  <div><span className="text-zinc-500">Action:</span> <span className="text-zinc-50">{selectedItem.action}</span></div>
                  <div><span className="text-zinc-500">Target:</span> <span className="text-zinc-300 font-mono">{selectedItem.target_collection || "—"}</span></div>
                  <div><span className="text-zinc-500">Target ID:</span> <span className="text-zinc-300 font-mono text-xs">{selectedItem.target_id || "—"}</span></div>
                  <div><span className="text-zinc-500">Status:</span> <Badge className={cn("text-xs", selectedItem.status === "pending" && "bg-yellow-500/20 text-yellow-400", selectedItem.status === "accepted" && "bg-emerald-500/20 text-emerald-400", selectedItem.status === "rejected" && "bg-red-500/20 text-red-400")}>{selectedItem.status}</Badge></div>
                  <div><span className="text-zinc-500">Date:</span> <span className="text-zinc-300">{selectedItem.created_at?.slice(0, 19).replace("T", " ")}</span></div>
                </div>
                {selectedItem.description && (
                  <div>
                    <p className="text-zinc-500 text-xs mb-1">Description</p>
                    <p className="text-zinc-300 text-sm bg-zinc-800/50 p-3 rounded">{selectedItem.description}</p>
                  </div>
                )}
                <div>
                  <p className="text-zinc-500 text-xs mb-1">Payload</p>
                  <pre className="text-xs text-zinc-300 bg-zinc-800/80 p-3 rounded overflow-x-auto max-h-60">{JSON.stringify(selectedItem.payload, null, 2)}</pre>
                </div>
                {selectedItem.reviewed_by && (
                  <div className="text-xs text-zinc-500">
                    Reviewed by {selectedItem.reviewed_by} at {selectedItem.reviewed_at?.slice(0, 19).replace("T", " ")}
                    {selectedItem.reject_reason && <span className="text-red-400"> — {selectedItem.reject_reason}</span>}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-700">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">Reject Item</DialogTitle>
            </DialogHeader>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleReject}>Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SandboxPage;
